import { navigateToWorkbench } from 'app/routes';
import {
    LIST_MODELS,
    LIST_MODEL_TAGS,
    VALIDATE_MODEL_JOBS,
    UPDATE_MODEL_JOBS,
    GET_MODEL_TAG_OUTPUT_COLUMNS,
    DEPLOY_MODEL,
    REDEPLOY_MODEL,
    EXECUTE_QUERY_BY_SERVICE_ACCOUNT,
    EXECUTE_QUERY_BY_ENVIRONMENT,
    GET_LAST_JOB_RUN
} from 'app/lib/types/server';
import { EXECUTE_ARBITRARY_SQL_OPERATION } from 'app/store/__generated__/operations';
import { selectProjectId, selectProjectSlug } from 'app/store/projects/selectors.js';
import { doLoadCurrentProject } from 'app/store/projects/actions.js';
import { doTriggerJobRun } from 'app/store/jobs/actions';
import { selectEntitiesById } from 'app/store/entities/selectors.js';
import {
    TOGGLE_CREATE_JOB_DIALOG,
    LIST_MODELS_ACTION_TYPE,
    VALIDATE_JOBS_ACTION_TYPE,
    UPDATE_JOBS_ACTION_TYPE,
    LIST_TAGS_ACTION_TYPE,
    GET_OUTPUT_COLUMNS_ACTION_TYPE,
    TOGGLE_DEPLOY_MODEL_DIALOG,
    DEPLOY_MODEL_ACTION_TYPE,
    REDEPLOY_MODEL_ACTION_TYPE,
    LIST_ENVIRONMENT_DATABASES,
    LIST_ENVIRONMENT_SCHEMAS,
    LIST_ENVIRONMENT_ROLES,
    LIST_ENVIRONMENT_WAREHOUSES,
    CONVERT_TO_MODEL_ACTION_TYPE,
    TOGGLE_CONVERT_TO_MODEL_DIALOG,
    QUEUE_DEPLOYMENTS_FOR_CONVERSION,
    CLOSE_CONVERT_ALL_DIALOG,
    OPEN_CONVERT_ALL_DIALOG,
    GET_PROJECT_MODELS_JOB_RUNS,
    GET_LAST_MODEL_JOB_RUN,
    LIST_DATABASES,
    LIST_SCHEMAS
} from './types.js';
import { selectNextEntityIdToConvert, selectModelsData } from './selectors.js';

export const doToggleCreateJobDialog = (tag) => ({
    type: TOGGLE_CREATE_JOB_DIALOG,
    meta: { tag }
});

export const doToggleDeployModelDialog = (payload) => ({
    type: TOGGLE_DEPLOY_MODEL_DIALOG,
    meta: { entityId: payload?.entityId }
});

export const doListTags = ({ projectId, modelId }) => ({
    type: LIST_TAGS_ACTION_TYPE,
    api: { version: 2, name: LIST_MODEL_TAGS },
    params: { projectId, modelId }
});

export const doListModels = (params) => ({
    type: LIST_MODELS_ACTION_TYPE,
    api: { version: 2, name: LIST_MODELS },
    params
});

export const doValidateJobs = ({ definitionIds, targetTagId }) => ({
    type: VALIDATE_JOBS_ACTION_TYPE,
    api: { version: 2, name: VALIDATE_MODEL_JOBS },
    params: { definitionIds, targetTagId }
});

export const doUpdateJobs = ({ definitionIdsToUpdate, targetTagId, allDefinitionIds }) => ({
    type: UPDATE_JOBS_ACTION_TYPE,
    api: { version: 2, name: UPDATE_MODEL_JOBS },
    params: { definitionIds: definitionIdsToUpdate, newTagId: targetTagId },
    onSucceeded: ({ dispatch }) => {
        dispatch(
            doValidateJobs({
                definitionIds: allDefinitionIds ?? definitionIdsToUpdate,
                targetTagId
            })
        );
    }
});

export const doGetOutputColumns = ({ tagId }) => ({
    type: GET_OUTPUT_COLUMNS_ACTION_TYPE,
    api: { version: 2, name: GET_MODEL_TAG_OUTPUT_COLUMNS },
    params: { tagId }
});

const onDeployModelSucceeded = ({ dispatch, payload, getState, responseName }) => {
    const state = getState();
    const viewRef = payload?.viewRef;
    const spaceSlug = selectProjectSlug(state);
    const projectId = selectProjectId(state);

    dispatch(doLoadCurrentProject());
    if (viewRef) {
        navigateToWorkbench(spaceSlug, viewRef.viewSlug);
    }
    if (responseName !== 'DeployedNodeMigrated') {
        dispatch(doTriggerJobRun(payload?.jobDefUid));
    }
    dispatch(doListTags({ projectId }));
};

export const doDeployModel = ({ targetTable, tagMessage, operationNodeUuid }) => ({
    type: DEPLOY_MODEL_ACTION_TYPE,
    api: { version: 2, name: DEPLOY_MODEL },
    params: { targetTable, tagMessage, operationNodeUuid },
    meta: { targetTable },
    onSucceeded: onDeployModelSucceeded
});

export const doRedeployModel = ({ nodeUuid, tagMessage, tagName, targetTable }) => ({
    type: REDEPLOY_MODEL_ACTION_TYPE,
    api: { version: 2, name: REDEPLOY_MODEL },
    params: { nodeUuid, tagMessage, tagName },
    meta: { targetTable },
    onSucceeded: onDeployModelSucceeded
});

export const doListDatabases = () => ({
    type: LIST_DATABASES,
    api: { ...EXECUTE_ARBITRARY_SQL_OPERATION, sync: true },
    params: { sql: 'SHOW TERSE DATABASES' }
});

export const doListSchemas = () => ({
    type: LIST_SCHEMAS,
    api: { ...EXECUTE_ARBITRARY_SQL_OPERATION, sync: true },
    params: { sql: 'SHOW TERSE SCHEMAS' }
});

export const doListEnvironmentDatabases = (params) => ({
    type: LIST_ENVIRONMENT_DATABASES,
    api: {
        version: 2,
        name: params.environmentUid
            ? EXECUTE_QUERY_BY_ENVIRONMENT
            : EXECUTE_QUERY_BY_SERVICE_ACCOUNT
    },
    params: { ...params, sql: 'SHOW TERSE DATABASES' }
});

export const doListEnvironmentSchemas = (params) => ({
    type: LIST_ENVIRONMENT_SCHEMAS,
    api: {
        version: 2,
        name: params.environmentUid
            ? EXECUTE_QUERY_BY_ENVIRONMENT
            : EXECUTE_QUERY_BY_SERVICE_ACCOUNT
    },
    params: { ...params, sql: 'SHOW TERSE SCHEMAS' }
});

export const doListEnvironmentRoles = (params) => ({
    type: LIST_ENVIRONMENT_ROLES,
    api: { version: 2, name: EXECUTE_QUERY_BY_SERVICE_ACCOUNT },
    params: { ...params, sql: 'SELECT CURRENT_AVAILABLE_ROLES() AS ROLES' }
});

export const doListEnvironmentWarehouses = (params) => ({
    type: LIST_ENVIRONMENT_WAREHOUSES,
    api: { version: 2, name: EXECUTE_QUERY_BY_SERVICE_ACCOUNT },
    params: { ...params, sql: 'SHOW WAREHOUSES' }
});

const doConvertToModel = ({ nodeUuid }) => ({
    type: CONVERT_TO_MODEL_ACTION_TYPE,
    api: { version: 2, name: 'MigrateDeployedNode' },
    params: { nodeUuid },
    meta: (state) => {
        const entity = selectEntitiesById(state)[nodeUuid];
        return { viewName: entity?.name };
    },
    onSucceeded: ({ dispatch, payload, getState, responseName }) => {
        const state = getState();
        const nextUuid = selectNextEntityIdToConvert(state);
        if (nextUuid) {
            dispatch(doConvertToModel({ nodeUuid: nextUuid }));
        } else {
            onDeployModelSucceeded({ dispatch, payload, getState, responseName });
        }
    }
});

export const doToggleConvertToModelDialog = (entityId) => ({
    type: TOGGLE_CONVERT_TO_MODEL_DIALOG,
    meta: { entityId }
});

export const doOpenConvertAllDialog = (deployments) => ({
    type: OPEN_CONVERT_ALL_DIALOG,
    payload: { deployments }
});

export const doCloseConvertAllDialog = () => ({
    type: CLOSE_CONVERT_ALL_DIALOG
});

export const doQueueDeploymentsForConversion = (entityIds) => ({
    type: QUEUE_DEPLOYMENTS_FOR_CONVERSION,
    payload: { entityIds },
    effect: ({ store }) => {
        const state = store.getState();
        const nextUuid = selectNextEntityIdToConvert(state);
        if (nextUuid) {
            store.dispatch(doConvertToModel({ nodeUuid: nextUuid }));
        }
    }
});

const doGetLastJobRun = (definitionUid, entityId) => ({
    type: GET_LAST_MODEL_JOB_RUN,
    api: { version: 2, name: GET_LAST_JOB_RUN },
    meta: { entityId },
    params: {
        _kind: GET_LAST_JOB_RUN,
        definitionUid
    }
});

export const doGetProjectModelsLastJobRuns = () => ({
    type: GET_PROJECT_MODELS_JOB_RUNS,
    effect: ({ store }) => {
        const state = store.getState();
        const models = selectModelsData(state);
        const entities = selectEntitiesById(state);

        models.forEach(({ projectUid, uid }) => {
            const nodeUuid = `${projectUid}.${uid}`;
            const entity = entities[nodeUuid];
            const jobDefUid = entity.jobDefUid;

            if (jobDefUid) {
                store.dispatch(doGetLastJobRun(jobDefUid, entity.entityId));
            }
        });
    }
});
